import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const SEVERITY_COLOR: Record<string, 'default' | 'warning' | 'error'> = {
  warning: 'warning',
  serious: 'error',
  critical: 'error',
};

const REVIEW_STATUSES = ['PENDING_REVIEW', 'CONFIRMED', 'DISMISSED', 'COACHING_REQUIRED', 'RESOLVED'];

const CallQaConductPage: React.FC = () => {
  const { user } = useAuth();
  const canRead = (user?.permissions || []).includes('call_qa.calls.read');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [severity, setSeverity] = useState('');
  const [reviewStatus, setReviewStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const params: Record<string, string> = {};
  if (severity) params.severity = severity;
  if (reviewStatus) params.review_status = reviewStatus;
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;

  const { data, isLoading } = useQuery({
    queryKey: ['callQaConductIncidents', params],
    queryFn: () => apiClient.callQa.listConductIncidents(params),
    enabled: canRead,
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      apiClient.callQa.updateConductIncident(id, { review_status: status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callQaConductIncidents'] });
    },
  });

  if (!canRead) {
    return <Alert severity="warning">Agent Conduct requires call_qa.calls.read.</Alert>;
  }

  const rows = data?.data?.data?.rows || [];
  const total = data?.data?.data?.total || 0;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Agent conduct toward customers — detected independently of QA scorecards. Every incident
        is an AI prompt that <strong>requires human verification</strong>, not a confirmed finding.
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Severity</InputLabel>
          <Select label="Severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="critical">Critical</MenuItem>
            <MenuItem value="serious">Serious</MenuItem>
            <MenuItem value="warning">Warning</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Review status</InputLabel>
          <Select label="Review status" value={reviewStatus} onChange={(e) => setReviewStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {REVIEW_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
          value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
        />
        <TextField
          size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
          value={dateTo} onChange={(e) => setDateTo(e.target.value)}
        />
      </Stack>

      {isLoading ? <CircularProgress size={24} /> : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Date', 'Agent', 'Campaign', 'Incident', 'Severity', 'Confidence', 'Evidence', 'Notification', 'Review', ''].map((h) => (
                  <TableCell key={h}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No conduct incidents match these filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : rows.map((r: any) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.call_started_at ? moment(r.call_started_at).format('MMM D h:mm A') : '—'}</TableCell>
                  <TableCell>{r.agent_name || 'Unlinked'}</TableCell>
                  <TableCell>{r.campaign_name || '—'}</TableCell>
                  <TableCell>
                    {r.category_label}
                    {r.ambiguous && <Chip size="small" label="ambiguous" sx={{ ml: 0.5 }} />}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={SEVERITY_COLOR[r.severity] || 'default'} label={r.severity} />
                  </TableCell>
                  <TableCell>{r.confidence != null ? `${Math.round(r.confidence * 100)}%` : '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 240 }}>
                    <Tooltip title={r.evidence_text || ''}>
                      <Typography variant="body2" noWrap>{r.evidence_text || '—'}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={r.notification_status === 'NOTIFIED' ? 'Emailed'
                        : r.notification_status === 'SUPPRESSED_AMBIGUOUS' ? 'Review only' : 'Not emailed'}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    <Select
                      size="small"
                      value={r.review_status}
                      onChange={(e) => updateStatus.mutate({ id: r.id, status: e.target.value })}
                      disabled={updateStatus.isPending}
                    >
                      {REVIEW_STATUSES.map((s) => (
                        <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => navigate(`/call-qa/calls/${r.call_id}?seek_ms=${r.start_ms || 0}`)}
                    >
                      Review
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        {total} incident{total === 1 ? '' : 's'} (most recent first). Conduct detection and storage
        continue even when conduct emails are disabled.
      </Typography>
    </Box>
  );
};

export default CallQaConductPage;
